import React, { useEffect, useRef, useState } from 'react';
import { CONSENT_CATEGORIES } from '../../config/consentCategories';
import Button from '../ui/Button';

const TITLE_ID = 'consent-preferences-title';

/**
 * The cookie preference center. Reachable via the banner's "Customise"
 * button and via the footer's "Consent Preferences" link at any time
 * afterward (see consentClient.js's openPreferences bridge).
 *
 * Dialog behavior copies RetroactiveConsentModal.js's focus-trap/scroll-lock
 * pattern. Unlike that modal (a required consent gate with no way out),
 * this one is optional and Escape-dismissible.
 */
const ConsentPreferencesModal = ({ open, currentConsent, onClose, onSave, onAcceptAll, onRejectAll }) => {
  const panelRef = useRef(null);
  const [choices, setChoices] = useState({
    analytics: !!currentConsent?.analytics,
    advertising: !!currentConsent?.advertising,
  });

  useEffect(() => {
    if (open) {
      setChoices({
        analytics: !!currentConsent?.analytics,
        advertising: !!currentConsent?.advertising,
      });
    }
  }, [open, currentConsent]);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative w-full max-w-lg card p-6 sm:p-8 focus:outline-none max-h-[85vh] overflow-y-auto"
      >
        <h2 id={TITLE_ID} className="text-lg font-semibold text-brand-ink mb-2">Cookie Preferences</h2>
        <p className="text-body text-secondary leading-relaxed mb-6">
          Choose which cookies we're allowed to use. Necessary cookies are
          always on, since the site can't function without them.
        </p>

        <div className="space-y-5 mb-6">
          {CONSENT_CATEGORIES.map((category) => (
            <div key={category.key} className="border-b border-brand-rose/20 pb-4 last:border-0 last:pb-0">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={category.required ? true : !!choices[category.key]}
                  disabled={category.required}
                  onChange={(e) =>
                    setChoices((prev) => ({ ...prev, [category.key]: e.target.checked }))
                  }
                />
                <span>
                  <span className="block font-medium text-brand-ink">
                    {category.label}
                    {category.required && <span className="text-secondary text-label ml-2">(always on)</span>}
                  </span>
                  <span className="block text-secondary text-label mt-0.5">{category.description}</span>
                </span>
              </label>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button type="button" variant="ghost" onClick={onRejectAll} className="flex-1">
            Reject all
          </Button>
          <Button type="button" variant="secondary" onClick={onAcceptAll} className="flex-1">
            Accept all
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => onSave(choices)}
            className="flex-1"
          >
            Save preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsentPreferencesModal;
